import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema } from "./pagination";

/**
 * HCA Moravia — matice klient × produkt (Sprint 46, §C-02 + DH-05).
 *
 * Runtime matrix bez vlastní DB tabulky. API volá jednu query s CTE
 * nad klient / přihláška / docházka / obchodní případy / produkty.
 * Každý buňka klient×produkt má stav:
 * - ABSOLVOVANO — klient má pritomen=true dochazka
 * - PRIHLASEN — má přihlášku bez docházky nebo pritomen=false
 * - V_PIPELINE — má obchodní případ ve fázi OSLOVENI/SCHUZKA/NABIDKA/UZAVRENO
 * - POTENCIAL — nic z výše, ale produkt je v seznamu (default)
 * - ZADNE — filter hidden pokud minStav != zadne
 */

export const matrixStavSchema = z.enum([
  "ZADNE",
  "POTENCIAL",
  "V_PIPELINE",
  "PRIHLASEN",
  "ABSOLVOVANO",
]);
export type MatrixStav = z.infer<typeof matrixStavSchema>;

const productTypeSchema = z.enum(["komunikace", "obchod", "leadership", "management", "ostatni"]);

/**
 * Filter input pro list procedury. Extends paginationSchema pro řádky
 * (klienti). Sloupce (produkty) se nevedou přes pagination — typicky
 * se jich pro UI vejde 20-80 najednou; filter productTypes je zúží.
 */
export const listMatrixSchema = paginationSchema.extend({
  firmaId: optionalUuid("Neplatné ID firmy"),
  tagIds: z.array(z.string().uuid("Neplatné ID štítku")).max(20).optional(),
  productTypes: z.array(productTypeSchema).max(5).optional(),
  /** Filter řádků — jen klienti s alespoň touto úrovní v nějaké buňce. */
  minStav: matrixStavSchema.optional(),
  /** Fulltext na jméno klienta nebo název firmy. */
  search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
});

export type ListMatrixInput = z.infer<typeof listMatrixSchema>;

export const matrixExportCsvSchema = listMatrixSchema;
export type MatrixExportCsvInput = z.infer<typeof matrixExportCsvSchema>;

/**
 * Helper pro rozlišení barevného tokenu v UI. Vrací Tailwind token
 * podle stavu — držíme mapping v shared, protože používáme i v CSV
 * exportu a budoucích email reportech.
 */
export function matrixStavColor(stav: MatrixStav): string {
  switch (stav) {
    case "ABSOLVOVANO":
      return "emerald";
    case "PRIHLASEN":
      return "blue";
    case "V_PIPELINE":
      return "amber";
    case "POTENCIAL":
      return "slate";
    case "ZADNE":
      return "transparent";
  }
}

/** Priority ordering pro minStav filter — ZADNE=0 → ABSOLVOVANO=4. */
export function matrixStavRank(stav: MatrixStav): number {
  switch (stav) {
    case "ZADNE":
      return 0;
    case "POTENCIAL":
      return 1;
    case "V_PIPELINE":
      return 2;
    case "PRIHLASEN":
      return 3;
    case "ABSOLVOVANO":
      return 4;
  }
}
